import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  CampaignProduct,
  CustomStepCategoryEnum,
  Funnel,
  FunnelInput,
  FunnelInputTypeEnum,
  StepButtonControlTypes,
  StepButtonShapeStyles,
  StepButtonShapes,
  StepButtonTypes,
  StepButtons,
  StepButtonsLayout,
  StepEditorIds,
  StepElements,
  StepEmulatorDevices,
  StepSectionWidths,
  User,
  buildPathVariables,
  transparent,
  whiteColor,
  DiscountSetting,
  ChoiceTypeCategories,
  WidgetVariable,
} from '../../../_models';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { StepEmulatorService, StepTemplateService, WidgetService } from '../../../_services';
import { addDummyValues, FontFamilyFormats, FontSizes, initializeEditorStyles, StepHints } from '../../step-util';
import { trigger, state, style, animate, transition } from '@angular/animations';
import * as tinymce from 'tinymce';
import { FormControlStatus } from '../../../_forms';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { FunnelInputFieldsComponent } from '../../../funnel-input';

@Component({
  selector: 'step-button',
  templateUrl: './step-button.component.html',
  styleUrls: ['./step-button.component.scss'],
  animations: [
    trigger('fadeIn', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate('500ms ease-in', style({ opacity: 1 }))]),
    ]),
  ],
  encapsulation: ViewEncapsulation.None,
})
export class StepButtonComponent implements OnInit, OnDestroy, OnChanges {
  @Input() layout: StepButtonsLayout;
  @Input() form: FormGroup;
  @Input() enableFormErrors: boolean;
  @Input() theme: string;
  @Input() useExternalButtons: boolean;
  @Input() funnel: Funnel;
  @Input() stepType: CustomStepCategoryEnum;
  @Input() templateId: string;
  @Input() stepId: string;
  @Input() product: CampaignProduct = {} as CampaignProduct;
  @Input() user: User;
  @Input() isSurveyStep: boolean;
  @Input() activeDevice: StepEmulatorDevices;
  @Input() discount: DiscountSetting;
  @Input() isSingleStepOffer: boolean = false;
  stepButtons: StepButtons;
  stepElements = StepElements;
  buttonShapes = StepButtonShapes;
  buttonTypes = StepButtonTypes;
  buttonControlTypes = StepButtonControlTypes;
  stepHints = StepHints;
  stepEditorIds = StepEditorIds;
  private subscription: Subscription;
  private widgetSubscription: Subscription;
  funnelPathVariables: { value: string; text: string }[] = [];
  buttonStyles = [
    { value: this.buttonShapes.Flat, label: 'Flat Button' },
    { value: this.buttonShapes.Modern, label: 'Modern Button' },
    { value: this.buttonShapes.Pill, label: 'Pill Button' },
    { value: this.buttonShapes.FlatOutline, label: 'Flat Outline' },
    { value: this.buttonShapes.ModernOutline, label: 'Modern Outline' },
    { value: this.buttonShapes.PillOutline, label: 'Pill Outline' },
    { value: this.buttonShapes.TextOnly, label: 'Text Only' },
  ];
  borderWidths = [
    { value: '1px', label: '1px' },
    { value: '2px', label: '2px' },
    { value: '3px', label: '3px' },
    { value: '4px', label: '4px' },
    { value: '5px', label: '5px' },
  ];
  outlineShapes = [StepButtonShapes.FlatOutline, StepButtonShapes.ModernOutline, StepButtonShapes.PillOutline];
  confirmControl: FormGroup;
  rejectControl: FormGroup;
  public selectedInput: FunnelInput = null;
  selectedInputIndex: number;
  @ViewChild(FunnelInputFieldsComponent, { static: false }) inputComponent: FunnelInputFieldsComponent;
  stepSectionWidths = StepSectionWidths;

  constructor(
    protected stepService: StepTemplateService,
    private stepEmulatorService: StepEmulatorService,
    public ngxSmartModalService: NgxSmartModalService,
    protected widgetService: WidgetService
  ) {}

  ngOnInit() {
    this.subscription = this.stepEmulatorService.stepButtons$.subscribe((data) => {
      this.stepButtons = data;
    });

    if (this.layout.rejectDisabled) {
      this.stepButtons.rejectButton.disabled = true;
    }

    this.loadWidgetVariables();
    this.applyTheme();

    const inputs = this.form.get('inputs') as FormArray;
    if (this.useExternalButtons) {
      if (
        (this.stepType === CustomStepCategoryEnum.AdjustShippingFrequency ||
          this.stepType === CustomStepCategoryEnum.PauseSurvey) &&
        !this.isSingleStepOffer
      ) {
        this.rejectControl = inputs.at(inputs.length - 1) as FormGroup;
      }
      this.confirmControl = (this.form.get('button_styles') as FormGroup).get('confirm') as FormGroup;
    } else {
      this.confirmControl = inputs.at(0) as FormGroup;
      this.rejectControl = inputs.at(1) as FormGroup;
    }

    this.initializeValues();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.theme && changes.theme.currentValue && this.stepButtons) {
      this.applyTheme();
    }

    if (
      ((changes.product && this.product) || (changes.discount && this.discount)) &&
      this.confirmControl &&
      this.rejectControl
    ) {
      this.onConfirmButtonChange(this.confirmControl.value.label);
      this.onRejectButtonChange(this.rejectControl.value.label);
    }
  }

  applyTheme() {
    const confirmButton = this.stepButtons.confirmButton,
      rejectButton = this.stepButtons.rejectButton;

    if (this.outlineShapes.includes(confirmButton.style)) {
      confirmButton.brandFont = this.theme;
      confirmButton.brandBorder = this.theme;
      confirmButton.brandBackground = whiteColor;
    } else if (confirmButton.style === StepButtonShapes.TextOnly) {
      confirmButton.brandFont = this.theme;
      confirmButton.brandBorder = transparent;
      confirmButton.brandBackground = transparent;
    } else {
      confirmButton.brandFont = whiteColor;
      confirmButton.brandBorder = whiteColor;
      confirmButton.brandBackground = this.theme;
    }

    if (this.outlineShapes.includes(rejectButton.style)) {
      rejectButton.brandFont = this.theme;
      rejectButton.brandBorder = this.theme;
      rejectButton.brandBackground = whiteColor;
    } else if (rejectButton.style === StepButtonShapes.TextOnly) {
      rejectButton.brandFont = this.theme;
      rejectButton.brandBorder = transparent;
      rejectButton.brandBackground = transparent;
    } else {
      rejectButton.brandFont = whiteColor;
      rejectButton.brandBorder = whiteColor;
      rejectButton.brandBackground = this.theme;
    }
  }

  initializeValues() {
    const inputs = this.form.get('inputs') as FormArray;
    const buttonStyles = this.form.get('button_styles') as FormGroup;
    const confirmStyles = buttonStyles.get('confirm') as FormGroup;

    const confirmButton = this.useExternalButtons ? confirmStyles.get('label').value : inputs.at(0).get('label').value;
    this.stepButtons.confirmButton.content = addDummyValues(
      confirmButton,
      this.funnelPathVariables,
      this.product,
      this.discount
    );

    const confirmStyle = this.useExternalButtons
      ? confirmStyles.get('style_type').value
      : inputs.at(0).get('button_styles').get('style_type').value;
    if (confirmStyle) {
      this.stepButtons.confirmButton.style = confirmStyle;
    }

    this.changeButtonShape(this.stepButtons.confirmButton.style, StepButtonTypes.Confirm);

    const confirmControl = this.useExternalButtons
      ? confirmStyles.get('control_type').value
      : inputs.at(0).get('button_styles').get('control_type').value;
    if (confirmControl) {
      this.stepButtons.confirmButton.type = confirmControl;
    }

    this.changeButtonControl(this.stepButtons.confirmButton.type, StepButtonTypes.Confirm);

    const confirmSettings = this.useExternalButtons
      ? confirmStyles.get('style').value
      : inputs.at(0).get('button_styles').get('style').value;
    if (confirmSettings) {
      this.extractButtonStyles(confirmSettings, StepButtonTypes.Confirm);
    }

    let rejectButton, rejectStyle, rejectControl, rejectSettings;
    if (this.useExternalButtons) {
      if (
        (this.stepType === CustomStepCategoryEnum.AdjustShippingFrequency ||
          this.stepType === CustomStepCategoryEnum.PauseSurvey) &&
        !this.isSingleStepOffer
      ) {
        rejectButton = inputs.at(inputs.length - 1).get('label').value;
        rejectStyle = inputs
          .at(inputs.length - 1)
          .get('button_styles')
          .get('style_type').value;
        rejectControl = inputs
          .at(inputs.length - 1)
          .get('button_styles')
          .get('control_type').value;
        rejectSettings = inputs
          .at(inputs.length - 1)
          .get('button_styles')
          .get('style').value;
      }
    } else if (inputs.length > 1) {
      rejectButton = inputs.at(1).get('label').value;
      rejectStyle = inputs.at(1).get('button_styles').get('style_type').value;
      rejectControl = inputs.at(1).get('button_styles').get('control_type').value;
      rejectSettings = inputs.at(1).get('button_styles').get('style').value;
    }

    this.stepButtons.rejectButton.content = addDummyValues(
      rejectButton,
      this.funnelPathVariables,
      this.product,
      this.discount
    );
    if (rejectStyle) {
      this.stepButtons.rejectButton.style = rejectStyle;
      this.changeButtonShape(this.stepButtons.rejectButton.style, StepButtonTypes.Reject);
    }
    if (rejectControl) {
      this.stepButtons.rejectButton.type = rejectControl;
      this.changeButtonControl(this.stepButtons.rejectButton.type, StepButtonTypes.Reject);
    }
    if (rejectSettings) {
      this.extractButtonStyles(rejectSettings, StepButtonTypes.Reject);
    }
  }

  toggleElement(element: StepElements) {
    this.stepEmulatorService.toggleElement(element);
  }

  isElementVisible(element: StepElements): boolean {
    return this.stepEmulatorService.isElementVisible(element);
  }

  buttonEditor = {
    height: 140,
    resize: false,
    plugins: 'line-height paste semibold',
    toolbar: 'bold semibold italic underline strikethrough | lineheightselect fontsizeselect fontselect',
    fontsize_formats: FontSizes,
    font_formats: FontFamilyFormats,
    paste_as_text: true,
    auto_focus: this.stepEditorIds.ConfirmButton,
    setup: (editor: tinymce.Editor) => {
      initializeEditorStyles(editor, '24px', "'Open Sans', Ubuntu, sans-serif");

      editor.on('Change KeyUp', () => {
        const content = editor.getContent();
        if (editor.id === StepEditorIds.ConfirmButton) {
          this.onConfirmButtonChange(content);
        } else if (editor.id === StepEditorIds.RejectButton) {
          this.onRejectButtonChange(content);
        }
      });
    },
  };

  onConfirmButtonChange(content: string) {
    if (this.stepButtons) {
      const updatedContent = addDummyValues(content, this.funnelPathVariables, this.product, this.discount);
      this.stepButtons.confirmButton.content = updatedContent;
    }
  }

  onRejectButtonChange(content: string) {
    if (this.stepButtons) {
      const updatedContent = addDummyValues(content, this.funnelPathVariables, this.product, this.discount);
      this.stepButtons.rejectButton.content = updatedContent;
    }
  }

  addConfirmButtonVariable(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value;
    const editorInstance = tinymce.get(this.stepEditorIds.ConfirmButton);
    if (editorInstance) {
      editorInstance.insertContent(selectedValue);
    }
    selectElement.selectedIndex = 0;
  }

  addRejectButtonVariable(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value;
    const editorInstance = tinymce.get(this.stepEditorIds.RejectButton);
    if (editorInstance) {
      editorInstance.insertContent(selectedValue);
    }

    selectElement.selectedIndex = 0;
  }

  changeButtonShape(style: StepButtonShapes, button: StepButtonTypes) {
    let brandFont, brandBorder, brandBackground;
    const styles = StepButtonShapeStyles[style];
    if (button === StepButtonTypes.Confirm) {
      /* Theme settings */
      if (this.outlineShapes.includes(+style)) {
        brandFont = this.theme;
        brandBorder = this.theme;
        brandBackground = whiteColor;
      } else if (+style === StepButtonShapes.TextOnly) {
        brandFont = this.theme;
        brandBorder = transparent;
        brandBackground = transparent;
      } else {
        brandFont = whiteColor;
        brandBorder = whiteColor;
        brandBackground = this.theme;
      }

      this.stepButtons.confirmButton = {
        ...this.stepButtons.confirmButton,
        ...styles,
        style: style,
        brandFont,
        brandBorder,
        brandBackground,
        shapesPopup: false,
      };
    } else if (button === StepButtonTypes.Reject) {
      /* Theme settings */
      if (this.outlineShapes.includes(+style)) {
        brandFont = this.theme;
        brandBorder = this.theme;
        brandBackground = whiteColor;
      } else if (+style === StepButtonShapes.TextOnly) {
        brandFont = this.theme;
        brandBorder = transparent;
        brandBackground = transparent;
      } else {
        brandFont = whiteColor;
        brandBorder = whiteColor;
        brandBackground = this.theme;
      }

      this.stepButtons.rejectButton = {
        ...this.stepButtons.rejectButton,
        ...styles,
        style: style,
        brandFont,
        brandBorder,
        brandBackground,
        shapesPopup: false,
      };
    }
  }

  changeButtonControl(type: StepButtonControlTypes, button: StepButtonTypes) {
    if (type === StepButtonControlTypes.Theme) {
      if (button === StepButtonTypes.Confirm) {
        this.stepButtons.confirmButton = {
          ...this.stepButtons.confirmButton,
          type: type,
          controlPopup: false,
        };
      } else if (button === StepButtonTypes.Reject) {
        this.stepButtons.rejectButton = {
          ...this.stepButtons.rejectButton,
          type: type,
          controlPopup: false,
        };
      }
    } else if (type === StepButtonControlTypes.Custom) {
      if (button === StepButtonTypes.Confirm) {
        this.stepButtons.confirmButton = {
          ...this.stepButtons.confirmButton,
          ...StepButtonShapeStyles[this.stepButtons.confirmButton.style],
          isFontDisabled: false,
          type: type,
          controlPopup: false,
        };
      } else if (button === StepButtonTypes.Reject) {
        this.stepButtons.rejectButton = {
          ...this.stepButtons.rejectButton,
          ...StepButtonShapeStyles[this.stepButtons.rejectButton.style],
          isFontDisabled: false,
          type: type,
          controlPopup: false,
        };
      }
    }
  }

  changeBorderWidth(borderWidth: string, button: StepButtonTypes) {
    if (button === StepButtonTypes.Confirm) {
      this.stepButtons.confirmButton.borderWidth = borderWidth;
      this.stepButtons.confirmButton.borderWidthPopup = false;
    } else if (button === StepButtonTypes.Reject) {
      this.stepButtons.rejectButton.borderWidth = borderWidth;
      this.stepButtons.rejectButton.borderWidthPopup = false;
    }
  }

  extractButtonStyles(style: string, button: StepButtonTypes) {
    const colorMatch = style.match(/color:\s*([^;]+)/);
    const backgroundColorMatch = style.match(/background-color:\s*([^;]+)/);
    const borderColorMatch = style.match(/border-color:\s*([^;]+)/);
    const borderRadiusMatch = style.match(/border-radius:\s*([^;]+)/);
    const borderWidthMatch = style.match(/border-width:\s*([^;]+)/);

    if (colorMatch) {
      if (button === StepButtonTypes.Confirm) {
        this.stepButtons.confirmButton.fontColor = colorMatch[1].trim();
      } else if (button === StepButtonTypes.Reject) {
        this.stepButtons.rejectButton.fontColor = colorMatch[1].trim();
      }
    }
    if (backgroundColorMatch) {
      if (button === StepButtonTypes.Confirm) {
        this.stepButtons.confirmButton.backgroundColor = backgroundColorMatch[1].trim();
      } else if (button === StepButtonTypes.Reject) {
        this.stepButtons.rejectButton.backgroundColor = backgroundColorMatch[1].trim();
      }
    }
    if (borderColorMatch) {
      if (button === StepButtonTypes.Confirm) {
        this.stepButtons.confirmButton.borderColor = borderColorMatch[1].trim();
      } else if (button === StepButtonTypes.Reject) {
        this.stepButtons.rejectButton.borderColor = borderColorMatch[1].trim();
      }
    }
    if (borderRadiusMatch) {
      if (button === StepButtonTypes.Confirm) {
        this.stepButtons.confirmButton.borderRadius = borderRadiusMatch[1].trim();
      } else if (button === StepButtonTypes.Reject) {
        this.stepButtons.rejectButton.borderRadius = borderRadiusMatch[1].trim();
      }
    }
    if (borderWidthMatch) {
      if (button === StepButtonTypes.Confirm) {
        this.stepButtons.confirmButton.borderWidth = borderWidthMatch[1].trim();
      } else if (button === StepButtonTypes.Reject) {
        this.stepButtons.rejectButton.borderWidth = borderWidthMatch[1].trim();
      }
    }
  }

  openInputsPopup(input, index) {
    this.selectedInput = input;
    this.selectedInputIndex = index;
    let _this = this;
    setTimeout(() => {
      _this.ngxSmartModalService.getModal('buttonActions').open();
    })
  }

  onCloseInputsPopup() {
    const input = (this.form.get('inputs') as FormArray).at(this.selectedInputIndex) as FormControl;
    const actions = input.get('actions') as FormArray;
    const data = this.inputComponent.getFormData();

    actions.clear();

    data.actions.forEach((action) => {
      actions.push(new FormControl(action));
    });
    this.ngxSmartModalService.getModal('buttonActions').close();
  }

  get hasChoiceLayoutOptions(): boolean {
    const category = this.form.get('category') && this.form.get('category').value;
    const isChoiceCategory = category && ChoiceTypeCategories.includes(category);
    return (this.form.get('type') && this.form.get('type').value === FunnelInputTypeEnum.Choice) && isChoiceCategory;
  }

  get isCustomConfirmButton() {
    return this.stepButtons.confirmButton.type === StepButtonControlTypes.Custom;
  }

  get isCustomRejectButton() {
    return this.stepButtons.rejectButton.type === StepButtonControlTypes.Custom;
  }

  get isConfirmFontDisabled() {
    return (
      this.stepButtons.confirmButton.type === StepButtonControlTypes.Theme &&
      (this.outlineShapes.includes(+this.stepButtons.confirmButton.style) ||
        this.stepButtons.confirmButton.style === StepButtonShapes.TextOnly)
    );
  }

  get isRejectFontDisabled() {
    return (
      this.stepButtons.rejectButton.type === StepButtonControlTypes.Theme &&
      (this.outlineShapes.includes(+this.stepButtons.rejectButton.style) ||
        this.stepButtons.rejectButton.style === StepButtonShapes.TextOnly)
    );
  }

  get confirmInvalidity() {
    return this.enableFormErrors && this.confirmControl.controls.label.status === FormControlStatus.Invalid;
  }

  get rejectInvalidity() {
    if (!this.rejectControl) {
      return false;
    }
    return this.enableFormErrors && this.rejectControl.controls.label.status === FormControlStatus.Invalid;
  }

  getWidthControl(type: StepSectionWidths.AcceptButtonContent | StepSectionWidths.RejectButtonContent): FormControl {
    const deviceKey = this.activeDevice.toString();
    return this.form.get('content_config').get(type).get(deviceKey) as FormControl;
  }

  openColorPicker(colorInput: any) {
    colorInput.click();
  }

  loadWidgetVariables() {
    this.widgetSubscription = this.widgetService
      .getProductWidgetVariables()
      .subscribe((widgets: WidgetVariable[]) => {
        this.funnelPathVariables = buildPathVariables(widgets);
      });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.widgetSubscription) {
      this.widgetSubscription.unsubscribe();
    }
  }
}
